import { generateUUID } from '@ps/utils/game'
import { batchCopyFile, saveFile, saveMultiFiles } from '@ps/api/editor/file'
import { Base64 } from 'js-base64'
import assets from '@ps/bridge/qc/assets'
import { getFileExtension, getFileName, removeFileExtension } from '@com/utils/file'
import { AssetRoute } from '@ps/utils/config'
import _ from 'lodash'
import { cookImageBinContent } from '@ps/bridge/qc/bin'
import qc from 'qc'
import { isSequenceFrame } from '@ps/bridge/qc/base'
import * as movieClipScript from '@ps/bridge/qc/scripts/movie-clip'
import { setAllBinFiles, setCurrentActionBinFiles } from '@ps/bridge/qc/scripts/movie-clip'
import generateId from '@com/utils/generate-id'
import { saveGlobalUrlMap } from '@ps/bridge/resource/global-url-map'

export type XlzFileDetail = {
  name: string
  dataUrl: string
}

/**
 * 生成序列帧文件夹路径
 */
export function productXlzDir(): string {
  const randUuid = generateUUID()
  return `${AssetRoute.GAME_ROOT}xlz_${randUuid}`
}

/**
 * 复制序列帧节点资源，返回新的 bin 文件列表
 * todo：提取保存序列帧 bin 文件、png 资源与更新资源列表的逻辑，可以给添加序列帧节点、粘贴序列帧节点两处复用
 * 1、生成 bin 文件
 * 2、保存 bin 文件与复制图片
 * 3、更新资源列表
 * 4、更新序列帧节点对应的 bin 文件配置
 */
export async function copyXlzNodeResource({
  node,
  dir,
  pid
}: {
  // 新复制出来的节点
  node: qc.Node
  // 目标路径
  dir: string
  pid: number
}): Promise<{
  binFiles: {
    data: string
    path: string
    uuid: string
  }[]
  node: qc.Node
} | void> {
  if (!isSequenceFrame(node)) throw new Error('node is not sequence frame')
  const mv = movieClipScript.getScript(node)
  if (mv && mv.data && mv.data.allFramePlaySmart) {
    const binPaths = mv.data.allFramePlaySmart.split(',').filter(Boolean)
    if (_.isEmpty(binPaths)) return

    const pngPaths = binPaths.map(item => item.replace('.bin', '.png'))
    const binFiles = pngPaths.map(item => {
      const { binName, binContent, uuid } = cookImageBinContent({
        name: getFileName(item)
      })
      return {
        data: Base64.encode(binContent),
        path: `${dir}${binName}`,
        uuid
      }
    })

    const res = await Promise.all([
      batchCopyFile({
        batch: [...pngPaths].map(src => {
          return {
            src,
            dst: dir
          }
        }),
        overwrite: 1,
        pid
      }),
      saveMultiFiles({
        pid,
        files: binFiles.map(item => ({
          data: item.data,
          path: item.path
        }))
      })
    ])

    if (res.every(item => item.code === 0)) {
      // 复制后，需要更新资源列表
      const newUuid2Path: {
        [key: string]: string
      } = {}
      binFiles.forEach(item => {
        newUuid2Path[item.uuid] = item.path
      })
      assets.uuid2UrlConf = {
        ...assets.uuid2UrlConf,
        ...newUuid2Path
      }
      await saveGlobalUrlMap({ projectKey: pid, uuid2url: assets.uuid2UrlConf })
      // 更新序列帧节点对应的 bin 文件配置
      const bins = binFiles.map(item => item.path).join(',')
      setAllBinFiles(node, bins)
      setCurrentActionBinFiles(node, bins)

      return {
        binFiles,
        node
      }
    }
    throw new Error('copy xlz resource error')
  }
}

/**
 * 检查 xlz 图片是否是以数字结尾
 * @param fileList
 */
export function isAllFileNameLegal(fileList: XlzFileDetail[]): boolean {
  return fileList.every(file => {
    const regex = /\d+$/g
    return Boolean(removeFileExtension(file.name).match(regex))
  })
}

/**
 * 生成序列帧图片名字，如 abc1.png 返回 x_image_随机字符串_1.png
 * @param name 原来的名字
 */
const generateXlzImageName = (name: string): string => {
  const regex = /\d+$/g
  const found = removeFileExtension(name).match(regex)
  if (found && found[0]) {
    return `x_image_${generateId()}_${found[0]}`
  }
  throw new Error('The name must end with a number')
}

/**
 * 在添加序列帧之前先修改文件名字
 * https://confluence.mobvista.com/pages/viewpage.action?pageId=58830795
 */
export function renameBeforeAddXlz(
  fileList: XlzFileDetail[]
): XlzFileDetail[] {
  const isLegal = isAllFileNameLegal(fileList)
  return fileList.map((file, index) => {
    const name = file.name
    const extension = getFileExtension(file.name)
    return {
      ...file,
      name: isLegal
        ? `${generateXlzImageName(name)}.${extension}`
        : `x_image_${generateId()}_${index + 1}.${extension}`
    }
  })
}

export async function addXlzToPlayable({
  fileList,
  pid,
  pathPrefix
}: {
  fileList: XlzFileDetail[]
  pid: number
  pathPrefix: string
}): Promise<{ fileList: XlzFileDetail[] } | void> {
  const newUuid2Path: {
    [key: string]: string
  } = {} // 存储uuid与path的映射
  const promiseList = fileList.map(async(file: XlzFileDetail) => {
    const fileName = file.name
    const dataUrl = file.dataUrl
    // 转换为 bin 格式后上传
    const { binName, binContent } = cookImageBinContent({
      name: fileName
    })
    const parsedBinContent = JSON.parse(binContent)
    const { uuid } = JSON.parse(parsedBinContent[0])

    const pathBin = `${pathPrefix}/${binName}`
    const pathPic = `${pathPrefix}/${fileName}`
    newUuid2Path[uuid] = pathBin

    const res = await Promise.all([
      saveFile(
        {
          pid,
          path: pathBin,
          enc: 'base64'
        },
        Base64.encode(binContent)
      ),
      saveFile(
        {
          pid,
          path: pathPic,
          enc: 'base64'
        },
        dataUrl.replace(/data:.*base64,/, '')
      )
    ])
    // @ts-ignore
    return res.every(item => item.code === 0)
  })

  const res = await Promise.all(promiseList)
  if (res.every(item => item)) {
    assets.uuid2UrlConf = {
      ...assets.uuid2UrlConf,
      ...newUuid2Path
    }
    await saveGlobalUrlMap({ projectKey: pid, uuid2url: assets.uuid2UrlConf })
    return {
      fileList
    }
  }
}

export function addImageToXlz() {
  //
}

export function removeImageFromXlz() {
  //
}
